'use client'

import DateRangeFilter from '@/components/date-range-filter'
import PaginationControls from '@/components/pagination/PaginationControls'
import {
    createLedgerOrphanBilling,
    getLedgerPaginated,
    LedgerEntry,
    LedgerEntryType,
} from '@/lib/api/financial/ledger-api'
import {
    INTERNAL_TYPE_LABELS,
    resolveOperationLabel,
} from '@/lib/financial/ledger-translations'
import { useEffect, useState } from 'react'
import { MdClose, MdLink, MdList, MdRefresh } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const TYPE_LABELS = INTERNAL_TYPE_LABELS as Record<LedgerEntryType, string>

const TYPE_COLORS: Record<LedgerEntryType, string> = {
    CREDIT_PAYMENT: 'badge-success',
    FEE: 'badge-warning',
    TRANSFER_OUT: 'badge-info',
    REFUND: 'badge-error',
    CHARGEBACK: 'badge-error',
    SPLIT_IN: 'badge-success',
    SPLIT_OUT: 'badge-info',
    ADJUSTMENT: 'badge-ghost',
}

const PAGE_SIZE_OPTIONS = [25, 50, 100]

const formatCurrency = (cents: number, withSign = true): string => {
    const v = (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })
    if (!withSign) return v
    return cents >= 0 ? `+${v}` : v
}

const formatDateTime = (iso: string): string =>
    new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })

/** `2026-08-13` -> ISO do início (ou fim) daquele dia no fuso do navegador. */
const dayBoundaryIso = (day: string, edge: 'start' | 'end'): string =>
    new Date(
        `${day}T${edge === 'start' ? '00:00:00.000' : '23:59:59.999'}`
    ).toISOString()

/** "1.234,56" / "1234.56" / "1234" -> centavos. Retorna `null` se não for valor. */
const parseAmountToCents = (value: string): number | null => {
    const normalized = value
        .trim()
        .replace(/\s/g, '')
        .replace(/\./g, '')
        .replace(',', '.')
    if (!normalized) return null
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null
    return Math.round(Number(normalized) * 100)
}

export default function LedgerPage() {
    const navigate = useNavigate()
    const [entries, setEntries] = useState<LedgerEntry[]>([])
    const [loading, setLoading] = useState(true)
    const [typeFilter, setTypeFilter] = useState<LedgerEntryType | ''>('')
    const [range, setRange] = useState<{
        startDate?: string
        endDate?: string
    }>({})
    const [dateFilterKey, setDateFilterKey] = useState(0)
    const [minAmountInput, setMinAmountInput] = useState('')
    const [maxAmountInput, setMaxAmountInput] = useState('')
    // Valores já convertidos em centavos, aplicados com atraso para não disparar
    // uma busca a cada tecla digitada.
    const [amountFilter, setAmountFilter] = useState<{
        min?: number
        max?: number
    }>({})
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(PAGE_SIZE_OPTIONS[0])
    const [total, setTotal] = useState(0)
    const [linkingId, setLinkingId] = useState<number | null>(null)
    /** Cumulativo com os demais filtros: só o que não tem cobrança nem transferência. */
    const [withoutLinks, setWithoutLinks] = useState(false)
    /** Movimentações vinculadas nesta visita, destacadas na tabela após o reload. */
    const [justLinkedIds, setJustLinkedIds] = useState<Set<number>>(new Set())

    // Qualquer mudança de filtro volta para a primeira página: manter a página 7
    // com um recorte novo mostraria "nenhuma movimentação" sem motivo aparente.
    // O reset é feito junto com cada filtro (e não num efeito à parte) para não
    // disparar uma busca com a página antiga antes do reset chegar.
    useEffect(() => {
        const timer = setTimeout(() => {
            setAmountFilter({
                min: parseAmountToCents(minAmountInput) ?? undefined,
                max: parseAmountToCents(maxAmountInput) ?? undefined,
            })
            setPage(1)
        }, 500)
        return () => clearTimeout(timer)
    }, [minAmountInput, maxAmountInput])

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        typeFilter,
        range.startDate,
        range.endDate,
        amountFilter.min,
        amountFilter.max,
        withoutLinks,
        page,
        limit,
    ])

    const load = async () => {
        setLoading(true)
        try {
            const res = await getLedgerPaginated({
                type: typeFilter || undefined,
                from: range.startDate
                    ? dayBoundaryIso(range.startDate, 'start')
                    : undefined,
                to: range.endDate
                    ? dayBoundaryIso(range.endDate, 'end')
                    : undefined,
                minAmount: amountFilter.min,
                maxAmount: amountFilter.max,
                withoutLinks: withoutLinks || undefined,
                page,
                limit,
            })
            if (res.success && res.result) {
                setEntries(res.result.items)
                setTotal(res.result.total)
            }
        } finally {
            setLoading(false)
        }
    }

    /**
     * Cria o vínculo do crédito órfão — o mesmo que o webhook faz: atrela à
     * cobrança referenciada quando ela existe, senão cria a cobrança avulsa — e
     * recarrega para a linha já mostrar o vínculo.
     */
    const handleCreateLink = async (ledgerEntryId: number) => {
        setLinkingId(ledgerEntryId)
        try {
            const res = await createLedgerOrphanBilling(ledgerEntryId)
            if (res.success && res.result) {
                const { billingId, created, settled } = res.result
                toast.success(
                    created
                        ? `Cobrança avulsa #${billingId} criada e vinculada.`
                        : settled
                          ? `Vinculado à cobrança #${billingId} e baixa aplicada.`
                          : `Vinculado à cobrança #${billingId}.`
                )
                // O reload remonta a tabela inteira: sem marcar, o operador perde de
                // vista qual linha acabou de vincular.
                setJustLinkedIds(prev => new Set(prev).add(ledgerEntryId))
                await load()
            } else {
                toast.error(res.error || 'Não foi possível criar o vínculo.')
            }
        } finally {
            setLinkingId(null)
        }
    }

    const invalidMin =
        minAmountInput.trim() !== '' &&
        parseAmountToCents(minAmountInput) === null
    const invalidMax =
        maxAmountInput.trim() !== '' &&
        parseAmountToCents(maxAmountInput) === null
    const hasFilters =
        !!typeFilter ||
        !!range.startDate ||
        !!range.endDate ||
        !!minAmountInput ||
        !!maxAmountInput ||
        withoutLinks

    const clearFilters = () => {
        setTypeFilter('')
        setMinAmountInput('')
        setMaxAmountInput('')
        setAmountFilter({})
        setWithoutLinks(false)
        setRange({})
        // O preset fica dentro do DateRangeFilter; remontar é o jeito de devolvê-lo
        // a "Todo período" sem transformar o componente em controlado.
        setDateFilterKey(k => k + 1)
        setPage(1)
    }

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <MdList /> Extrato
                    </h1>
                    <p className="text-base-content/60 text-sm mt-1">
                        Movimentações da conta digital (recebimentos, taxas,
                        transferências, estornos).
                    </p>
                </div>
                <button
                    className="btn btn-sm btn-ghost"
                    onClick={load}
                    disabled={loading}
                >
                    <MdRefresh /> Atualizar
                </button>
            </div>

            <div className="card bg-base-100 shadow mb-4">
                <div className="card-body p-4 gap-4">
                    <DateRangeFilter
                        key={dateFilterKey}
                        variant="buttons"
                        onChange={next => {
                            setRange(next)
                            setPage(1)
                        }}
                    />

                    <div className="flex flex-wrap items-end gap-3">
                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs font-medium">
                                    Tipo
                                </span>
                            </label>
                            <select
                                className="select select-bordered select-sm w-52 mt-1"
                                value={typeFilter}
                                onChange={e => {
                                    setTypeFilter(
                                        e.target.value as LedgerEntryType | ''
                                    )
                                    setPage(1)
                                }}
                            >
                                <option value="">Todos</option>
                                {Object.entries(TYPE_LABELS).map(([k, l]) => (
                                    <option key={k} value={k}>
                                        {l}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs font-medium">
                                    Valor de (R$)
                                </span>
                            </label>
                            <input
                                type="text"
                                inputMode="decimal"
                                placeholder="0,00"
                                className={`input input-bordered input-sm w-32 mt-1 ${invalidMin ? 'input-error' : ''}`}
                                value={minAmountInput}
                                onChange={e =>
                                    setMinAmountInput(e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs font-medium">
                                    Valor até (R$)
                                </span>
                            </label>
                            <input
                                type="text"
                                inputMode="decimal"
                                placeholder="0,00"
                                className={`input input-bordered input-sm w-32 mt-1 ${invalidMax ? 'input-error' : ''}`}
                                value={maxAmountInput}
                                onChange={e =>
                                    setMaxAmountInput(e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs font-medium">
                                    Por página
                                </span>
                            </label>
                            <select
                                className="select select-bordered select-sm w-24 mt-1"
                                value={limit}
                                onChange={e => {
                                    setLimit(Number(e.target.value))
                                    setPage(1)
                                }}
                            >
                                {PAGE_SIZE_OPTIONS.map(size => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs font-medium">
                                    Vínculo
                                </span>
                            </label>
                            <label className="label cursor-pointer justify-start gap-2 mt-1 h-8">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-sm toggle-secondary"
                                    checked={withoutLinks}
                                    onChange={e => {
                                        setWithoutLinks(e.target.checked)
                                        setPage(1)
                                    }}
                                />
                                <span className="label-text text-sm">
                                    Sem vínculo
                                </span>
                            </label>
                        </div>

                        {hasFilters && (
                            <button
                                className="btn btn-sm btn-ghost"
                                onClick={clearFilters}
                            >
                                <MdClose /> Limpar filtros
                            </button>
                        )}
                    </div>

                    <p className="text-xs text-base-content/50">
                        O filtro de valor considera o valor absoluto do
                        movimento: a faixa vale tanto para créditos quanto para
                        débitos.
                    </p>
                </div>
            </div>

            <div className="card bg-base-100 shadow">
                <div className="card-body p-0">
                    {loading && (
                        <div className="flex items-center justify-center min-h-[200px]">
                            <div className="loading loading-spinner loading-lg text-secondary"></div>
                        </div>
                    )}
                    {!loading && entries.length === 0 && (
                        <div className="text-center py-12 text-base-content/60">
                            Nenhuma movimentação encontrada.
                        </div>
                    )}
                    {!loading && entries.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Tipo</th>
                                        <th>Descrição</th>
                                        <th>Vínculo</th>
                                        <th className="text-right">Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map(e => (
                                        <tr
                                            key={e.id}
                                            className={`cursor-pointer ${
                                                justLinkedIds.has(e.id)
                                                    ? 'bg-success/15 hover:bg-success/25'
                                                    : 'hover:bg-base-200'
                                            }`}
                                            onClick={() =>
                                                navigate(
                                                    `/financial/statement/${e.id}`
                                                )
                                            }
                                        >
                                            <td className="whitespace-nowrap">
                                                {formatDateTime(e.occurredAt)}
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge badge-sm ${TYPE_COLORS[e.type]}`}
                                                >
                                                    {resolveOperationLabel({
                                                        type: e.type,
                                                        raw: e.raw,
                                                    })}
                                                </span>
                                            </td>
                                            <td className="text-sm">
                                                {e.description || '—'}
                                            </td>
                                            <td
                                                className="text-xs"
                                                onClick={ev =>
                                                    ev.stopPropagation()
                                                }
                                            >
                                                {e.billingId && (
                                                    <a
                                                        href={`/financial/billings/${e.billingId}`}
                                                        className="link link-primary"
                                                    >
                                                        Cobrança #{e.billingId}
                                                    </a>
                                                )}
                                                {e.transferId && (
                                                    <a
                                                        href={`/financial/transfers/${e.transferId}`}
                                                        className="link link-primary"
                                                    >
                                                        Transferência #
                                                        {e.transferId}
                                                    </a>
                                                )}
                                                {!e.billingId &&
                                                    !e.transferId &&
                                                    (e.amount > 0 ? (
                                                        <button
                                                            className="btn btn-xs btn-outline btn-primary whitespace-nowrap"
                                                            onClick={() =>
                                                                handleCreateLink(
                                                                    e.id
                                                                )
                                                            }
                                                            disabled={
                                                                linkingId ===
                                                                e.id
                                                            }
                                                        >
                                                            {linkingId ===
                                                            e.id ? (
                                                                <span className="loading loading-spinner loading-xs" />
                                                            ) : (
                                                                <MdLink />
                                                            )}
                                                            Criar vínculo
                                                        </button>
                                                    ) : (
                                                        '—'
                                                    ))}
                                            </td>
                                            <td
                                                className={`text-right font-mono whitespace-nowrap ${e.amount >= 0 ? 'text-success' : 'text-error'}`}
                                            >
                                                {formatCurrency(e.amount)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {total > 0 && (
                        <div className="px-4 border-t border-base-200">
                            <PaginationControls
                                page={page}
                                limit={limit}
                                total={total}
                                loading={loading}
                                onPageChange={setPage}
                                label="movimento"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
